import React, { FC, useState, useEffect } from 'react';
import { Box, Text, Flex } from 'rebass/styled-components';
import styled from 'styled-components';
import Upload from '../Upload';
import firebase from 'firebase';
import { LoadingBar } from '../loadingBar';
import uniqid from 'uniqid';
import useGallery from 'hooks/useGallery';
import GalleryDropzone from './galleryDropzone';
import Modal from '../modal';
import { checkBeforeUpload } from 'util/duplicates';
import ImageViewer, { ViewerImage } from './imageViewer';

// UI refresh (5 Nov 2026): rounded image cards with a 16:9 frame and an inline name field.
const ImageInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0 8px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: #2e333b;
  text-align: center;
  :hover,
  :focus {
    border-color: #b9bec6;
    background: #f9fafb;
    outline: none;
  }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  padding: 6px 6px 20px;
`;

const Thumbnail = styled.div`
  border: 1px solid #e4e6ea;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const ThumbnailImage = styled.img`
  cursor: zoom-in;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
  background: #16181d;
`;

const GalleryContainer: FC<{
  projectID: string;
  /** The project's own image, shown first (Aaron, 5 Nov 2026). It isn't a gallery record: it lives on the project. */
  heroURL?: string;
  files: IAsset[];
  isImagesLoading: boolean;
  isUploading(state: boolean): void;
}> = ({ projectID, heroURL, files, isImagesLoading }) => {
  const [viewing, setViewing] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const { uploadImages, updateImages } = useGallery();
  const [uploads, _setUploads] = useState<{ task: firebase.storage.UploadTask; id: string; fileName: string }[]>([]);
  const uploadsRef = React.useRef<{ task: firebase.storage.UploadTask; id: string; fileName: string }[]>(uploads);
  const setUploads = (uploads: { task: firebase.storage.UploadTask; id: string; fileName: string }[]) => {
    uploadsRef.current = uploads;
    _setUploads(uploads);
  };

  const [imageIds, setImageIds] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const initialImageIds = files.reduce((acc, image) => {
      acc[image.imageId!] = image.id;
      return acc;
    }, {} as { [key: string]: string });

    setImageIds(initialImageIds);
  }, [files, uploading]);

  const uploadMultipleFiles = async (dropped: File) => {
    const file = await checkBeforeUpload('gallery', dropped, projectID);
    if (!file) return;
    setUploading(true);
    // Stored at its original size, so it can be saved at full resolution (Aaron, 5 Nov 2026). Until
    // then gallery uploads were shrunk to 800px; those images stay as they were.
    try {
      uploadImages(file, projectID, (task) => {
        setUploads([...uploadsRef.current, { task: task, id: uniqid(), fileName: file.name }]);
      }).then((res) => {
        if (res) {
          setImageIds((prevState) => ({
            ...prevState,
            [res.toString()!]: file.name,
          }));
        }
      });
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  // What the viewer steps through: the project image, then every gallery image that has a file.
  const viewerImages: ViewerImage[] = [
    ...(heroURL ? [{ key: 'hero', name: 'Project image', url: heroURL, note: 'the project image' }] : []),
    ...files
      .map((image, index) => ({ image, index }))
      .filter(({ image }) => !!image.downloadURL)
      .map(({ image, index }) => ({
        key: `img-${image.imageId || index}`,
        name: (image.imageId && imageIds[image.imageId]) || image.id || 'Image',
        url: image.downloadURL as string,
      })),
  ];

  const handleInputChange = (id: string, value: string) => {
    setImageIds((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const updateImageName = (imageId: string, updatedName: string) => {
    updateImages(imageId, { id: updatedName });
  };

  return (
    <>
      <Box bg={'white'} width={1} height={'100%'}>
        <LoadingBar loading={uploading || isImagesLoading} />
        <Flex height={'100%'} flexDirection="column" style={{ flexGrow: 1 }}>
          {uploads.map((upload, index) => (
            <Upload key={`video${index}`} upload={upload.task} fileName={upload.fileName} uploading={setUploading} />
          ))}
          <GalleryDropzone upload={uploadMultipleFiles}>
            <Gallery>
              {/* The project's own image, first: always in the gallery, nothing copied (Aaron, 5 Nov 2026) */}
              {heroURL && (
                <Thumbnail>
                  <ThumbnailImage src={heroURL} alt="Project image" loading="lazy" onClick={() => setViewing(0)} title="View" />
                  <div style={{ padding: 4, height: 32, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#5B616E' }}>
                    Project image
                  </div>
                </Thumbnail>
              )}
              {files.map((image, index) => (
                <React.Fragment key={`${image.id}+${index}`}>
                  <Thumbnail>
                    {image?.downloadURL && (
                      <ThumbnailImage
                        src={image.downloadURL}
                        alt={image.id}
                        loading="lazy"
                        title="View"
                        onClick={() => {
                          const at = viewerImages.findIndex((v) => v.key === `img-${image.imageId || index}`);
                          if (at >= 0) setViewing(at);
                        }}
                      />
                    )}
                    <div style={{ padding: 4 }}>
                      <ImageInput
                        aria-label="Image name"
                        value={imageIds[image.imageId!] || ''}
                        type="text"
                        placeholder="Name this image"
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => updateImageName(image.imageId!, e.target.value)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(image.imageId!, e.target.value)}
                      />
                    </div>
                  </Thumbnail>
                </React.Fragment>
              ))}
            </Gallery>
          </GalleryDropzone>
        </Flex>
      </Box>
      {viewing !== null && viewerImages[viewing] && (
        <Modal>
          <ImageViewer images={viewerImages} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />
        </Modal>
      )}
    </>
  );
};

export default GalleryContainer;
